import {
    createAction,
    createAsyncThunk,
    createSelector,
    createSlice,
    Draft,
    isAnyOf,
} from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import { Permissions, MemberType } from "@nova/constants"
import { memoize } from "nodash"
import { asyncConcurrency } from "@nova/promise-wait"
import {
    MemberExtended,
    OrganizationPermissions,
    OrganizationPermissionsInput,
    WorkspaceMember,
} from "@nova/schemas"
import { membersApi as api } from "./membersApi"
import { fetchMe } from "user/profile/thunks"
import {
    getAllWorkspaces,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"

export type MemberAPI = {
    listMembersForWorkspace: (workspaceId: string, filter?: MemberType) => Promise<MemberExtended[]>
    createAllInvites: (
        workspaceId: string,
        userIds: string[],
        permissions: OrganizationPermissions
    ) => Promise<WorkspaceMember[]>
    updateMember: (
        workspaceId: string,
        userId: string,
        attributes: {
            type?: MemberType
            userSettings?: { name: string; value: string | boolean }[] // TODO what is this
            permissions?: OrganizationPermissionsInput
            favorites?: string[]
            recentId?: string // string of folder/project accessed. Server will update the recents object with UTC date.
            recentSearch?: { pluginName: string; data: unknown }
        }
    ) => Promise<WorkspaceMember>
    updateMemberAccessRequest: (
        userId: string,
        accessRequestId: string,
        workspaceId?: string,
        projectId?: string,
        removeRequest?: boolean
    ) => Promise<WorkspaceMember>
    acceptMemberInvitations: (email: string, userId: string) => Promise<boolean>
    softDeleteMember: (workspaceId: string, userId: string, isSelf?: boolean) => Promise<boolean>
}

const name = "user-management-v1"

type SliceState = {
    workspaceIds: string[]
    workspaceMembers: Record<string, WorkspaceMember[]>
    userFullName?: string
    userId?: string
    activeMember?: WorkspaceMember
    inviteModalId?: string
    requestAccessModalId?: string
}

//selectors
const {
    selectWorkspaceIds,
    selectAllMembers,
    selectUserFullName,
    selectActiveMember,
    selectInviteModalId,
    selectRequestAccessModalId,
    selectAllWorkspaceMembers,
} = withRoot((r: Record<typeof name, SliceState>) => r[name], {
    selectWorkspaceIds: () => (s) => s.workspaceIds,
    selectAllMembers: () => (s) => s.workspaceMembers,
    selectUserFullName: () => (s) => s.userFullName,
    selectAllWorkspaceMembers: (id: string) => (s) => s.workspaceMembers[id],
    selectActiveMember: () => (s) => s.activeMember,
    selectInviteModalId: () => (s) => s.inviteModalId,
    selectRequestAccessModalId: () => (s) => s.requestAccessModalId,
})

// duplicated to keep logic stable, but this function is more explicit
export const selectAllRegisteredMembers = createSelector(
    [selectAllMembers(), selectActiveWorkspaceId()],
    (members, id) => (id ? members[id]?.filter((e) => e.type === MemberType.Member) || [] : [])
)

export const selectWorkspaceMembers = createSelector(
    [selectAllMembers(), selectActiveWorkspaceId()],
    (members, id) => (id ? members[id]?.filter((e) => e.type === MemberType.Member) || [] : [])
)

export const selectWorkspaceInvites = memoize((orgId) =>
    createSelector([selectAllWorkspaceMembers(orgId)], (members) =>
        members.filter((e) => e.type === MemberType.Invite)
    )
)

const selectUserFirstName = createSelector(selectUserFullName(), (fullName) =>
    fullName?.split(" ").at(0)
)

const selectWorkspaceMemberIds = createSelector([selectWorkspaceMembers], (members) => {
    return members?.map((member) => member.userId)
})

export const selectWorkspaceMembersById = createSelector([selectWorkspaceMembers], (members) =>
    Object.fromEntries(members.map((member) => [member.userId, member]))
)

const listMembersForWorkspace = createAsyncThunk(
    `${name}/listMembersForWorkspace`,
    async ({ workspaceId }: { workspaceId: string }) => api.listMembersForWorkspace(workspaceId)
)

const listMembersForAllWorkspaces = createAsyncThunk(
    `${name}/listMembersForAllWorkspaces`,
    async ({ workspaceIds }: { workspaceIds: string[] }) => {
        const membersMap: Record<string, WorkspaceMember[]> = {}
        //this puts a lot of pressure on cognito so trying to minimize the concurrency
        await asyncConcurrency(workspaceIds, 2, async (id) => {
            const members = await api.listMembersForWorkspace(id)
            membersMap[id] = members
            return id
        })
        return membersMap
    }
)

const getWorkspaceWithMostMembers = createAsyncThunk(
    `${name}/getWorkspaceWithMostMembers`,
    async (ids: string[]) => {
        let max = 0
        let maxstring = ""
        await Promise.all(
            ids.map(async (id) => {
                const members = await api.listMembersForWorkspace(id)

                if (members && members.length > max) {
                    max = members.length
                    maxstring = id
                }
            })
        )
        return maxstring
    }
)

const inviteAllUsersToWorkspace = createAsyncThunk(
    `${name}/inviteAllUsersToWorkspace`,
    async ({
        workspaceId,
        emailAddresses,
        permissions,
    }: {
        workspaceId: string
        emailAddresses: string[]
        permissions: OrganizationPermissions
    }) => api.createAllInvites(workspaceId, emailAddresses, permissions)
)

const updateWorkspaceMember = createAsyncThunk(
    `${name}/updateWorkspaceMember`,
    async ({
        workspaceId,
        userId,
        attributes,
    }: {
        workspaceId: string
        userId: string
        attributes: {
            type?: MemberType
            userSettings?: { name: string; value: string | boolean }[]
            permissions?: OrganizationPermissions
            favorites?: string[]
            recentId?: string // string of folder/project accessed. Server will update the recents object with UTC date.
            recentSearch?: { pluginName: string; data: unknown }
        }
    }) => {
        return api.updateMember(workspaceId, userId, attributes)
    }
)

const updateWorkspaceMemberAccessRequest = createAsyncThunk(
    `${name}/updateWorkspaceMemberAccessRequest`,
    async ({
        workspaceId,
        projectId,
        userId,
        accessRequestId,
        removeRequest,
    }: {
        workspaceId?: string
        projectId?: string
        userId: string
        accessRequestId: string
        removeRequest?: boolean
    }) =>
        api.updateMemberAccessRequest(
            userId,
            accessRequestId,
            workspaceId,
            projectId,
            removeRequest
        )
)

const acceptInvitationsForUser = createAsyncThunk(
    `${name}/acceptInvitationsForUser`,
    async ({ email }: { email: string }) => api.acceptMemberInvitations(email)
)

const removeMemberFromWorkspace = createAsyncThunk(
    `${name}/removeMemberFromWorkspace`,
    async ({ workspaceId, userId }: { workspaceId: string; userId: string; isSelf?: boolean }) =>
        api.softDeleteMember(workspaceId, userId)
)

const updateOneMemberHandler = (
    state: Draft<SliceState>,
    data: ({ userId: string; organizationId: string } & Partial<WorkspaceMember>) | null
) => {
    if (!data) return
    const { organizationId, userId, ...newData } = data

    const members = state.workspaceMembers[organizationId]
    if (!members) {
        return
    }
    const filteredMembers = members.filter((member) => member.userId !== userId)
    const memberToUpdate = members.find((member) => member.userId === userId)
    if (!memberToUpdate) {
        return
    }
    const updatedMember = {
        ...memberToUpdate,
        ...newData,
        recents: newData.recents !== undefined ? newData.recents : memberToUpdate.recents,
        recentSearches:
            newData.recentSearches !== undefined
                ? newData.recentSearches
                : memberToUpdate.recentSearches,
    } as MemberExtended
    state.workspaceMembers[organizationId] = [...filteredMembers, updatedMember]
    state.activeMember = updatedMember
}

const updateMemberPermissionsHandler = createAsyncThunk(
    `${name}/updateMemberPermissions`,
    async ({
        workspaceId,
        member,
        newPermission,
        accessLevel,
        entityId,
        removeRequest,
    }: {
        workspaceId: string
        member: WorkspaceMember
        newPermission: Permissions
        accessLevel: "workspace" | "subRole"
        entityId?: string
        removeRequest?: boolean
    }) => {
        let existingPermissions = member.permissions
        if (removeRequest) {
            const accessIds = member.permissions.accessRequestIds
            const newIds = accessIds?.filter((id) => id !== entityId) || []
            existingPermissions = { ...existingPermissions, accessRequestIds: newIds }
        }
        if (accessLevel === "workspace") {
            if (newPermission === Permissions.None && member.permissions.subRoles.length === 0) {
                api.softDeleteMember(workspaceId, member.userId)
                return null
            }
            const newMember = await api.updateMember(workspaceId, member.userId, {
                permissions: {
                    ...existingPermissions,
                    workspaceAccess: newPermission,
                },
            })
            return newMember
        }
        if (!entityId) {
            throw "entityId is required if not updating workspace permissions"
        }

        const filteredSubPermissions =
            member.permissions?.subRoles?.filter((perm) => perm.id !== entityId) || []
        if (newPermission === Permissions.None) {
            //if we are removing their only sub permission, remove them from the workspace
            if (
                filteredSubPermissions.length === 0 &&
                member.permissions.workspaceAccess === Permissions.None
            ) {
                api.softDeleteMember(workspaceId, member.userId)
                return null
            }
            //otherwise just remove the one sub permission
            const newMember = await api.updateMember(workspaceId, member.userId, {
                permissions: {
                    ...existingPermissions,
                    subRoles: filteredSubPermissions,
                },
            })
            return newMember
        }
        const newSubPermission: OrganizationPermissionsInput = {
            subRoles: [...filteredSubPermissions, { id: entityId, permission: newPermission }],
            workspaceAccess: existingPermissions.workspaceAccess,
            accessRequestIds: existingPermissions.accessRequestIds,
        }
        const newPermissions = { ...existingPermissions, ...newSubPermission }
        const newMember = await api.updateMember(workspaceId, member.userId, {
            permissions: newPermissions,
        })

        return newMember
    }
)

const updateManyMemberPermissionsHandler = createAsyncThunk(
    `${name}/updateManyMemberPermissionHandler`,
    async ({
        workspaceId,
        member,
        newPermissions,
    }: {
        workspaceId: string
        member: WorkspaceMember
        newPermissions: OrganizationPermissions
    }) => {
        const { workspaceAccess, subRoles = [] } = newPermissions
        if (workspaceAccess === Permissions.None && subRoles.length === 0) {
            api.softDeleteMember(workspaceId, member.userId)
            return null
        }
        const newMember = await api.updateMember(workspaceId, member.userId, {
            permissions: { ...member.permissions, ...newPermissions },
        })
        return newMember
    }
)

const setActiveMember = createAction<WorkspaceMember | undefined>(`${name}/setActiveMember`)

const setInviteModalId = createAction<string | undefined>(`${name}/setInviteModalId`)

const setRequestModalId = createAction<string | undefined>(`${name}/setRequestAccessModalId`)

const addWorkspaceId = createAction<string>(`${name}/addWorkspaceId`)

const { reducer: r } = createSlice({
    name,
    initialState: {
        workspaceIds: [],
        workspaceMembers: {},
        userFullName: "",
    } as SliceState,
    reducers: {},
    extraReducers: (builder) =>
        builder
            .addCase(getAllWorkspaces.fulfilled, (state, action) => {
                const { organizations: workspaces } = action.payload
                const workspaceIds = []
                for (const workspace of workspaces) {
                    workspaceIds.push(workspace.id)
                }
                state.workspaceIds = workspaceIds
            })
            .addCase(listMembersForWorkspace.fulfilled, (state, action) => {
                const members = action.payload
                const workspaceId = action.meta.arg.workspaceId
                state.workspaceMembers[workspaceId] = members || []
            })
            .addCase(listMembersForAllWorkspaces.fulfilled, (state, action) => {
                const allMembers = action.payload
                state.workspaceMembers = allMembers
            })
            .addCase(inviteAllUsersToWorkspace.fulfilled, (state, action) => {
                // New invites get added; existing members came back with updated permissions
                const workspaceId = action.meta.arg.workspaceId
                for (const member of action.payload) {
                    if (member.type === MemberType.Invite) {
                        state.workspaceMembers[workspaceId].push(member)
                    } else {
                        updateOneMemberHandler(state, member)
                    }
                }
            })
            .addCase(updateWorkspaceMember.fulfilled, (state, action) => {
                const orgMembers = state.workspaceMembers[action.meta.arg.workspaceId]
                const member = orgMembers.find((e) => e.userId === action.meta.arg.userId)
                if (member) {
                    const { recentId } = action.meta.arg.attributes
                    const recents = recentId
                        ? {
                              ...(JSON.parse(member.recents || "{}") as Record<string, string>),
                              [recentId]: new Date().toISOString(),
                          }
                        : undefined
                    updateOneMemberHandler(state, {
                        ...action.payload,
                        ...(recents ? { recents: JSON.stringify(recents) } : {}),
                    })
                }
            })
            .addCase(removeMemberFromWorkspace.fulfilled, (state, action) => {
                const args = action.meta.arg
                const { workspaceId, userId } = args
                if (!action.payload) {
                    return
                }
                const isSelf = action.meta.arg.isSelf
                if (isSelf) {
                    const index = state.workspaceIds.indexOf(workspaceId)
                    state.workspaceIds = state.workspaceIds.splice(index, 1)
                }

                state.workspaceMembers[workspaceId] = state.workspaceMembers[workspaceId]?.filter(
                    (member) => member.userId !== userId
                )
            })
            .addCase(setActiveMember, (state, action) => {
                state.activeMember = action.payload
            })
            .addCase(setInviteModalId, (state, action) => {
                state.inviteModalId = action.payload
            })
            .addCase(setRequestModalId, (state, action) => {
                state.requestAccessModalId = action.payload
            })
            .addCase(addWorkspaceId, (state, action) => {
                state.workspaceIds.push(action.payload)
            })
            .addCase(updateWorkspaceMemberAccessRequest.fulfilled, (state, action) => {
                const updatedMemberPermissions = action.payload.permissions
                const workspaceId = action.payload.organizationId!

                if (
                    action.meta.arg.removeRequest &&
                    !updatedMemberPermissions?.workspaceAccess &&
                    !updatedMemberPermissions?.subRoles &&
                    new Set(updatedMemberPermissions?.accessRequestIds).size === 0
                ) {
                    //member was removed
                    state.workspaceMembers[workspaceId] = state.workspaceMembers[
                        workspaceId
                    ]?.filter((member) => member.userId !== action.payload.userId)
                    return
                }

                updateOneMemberHandler(state, action.payload)
            })
            .addCase(fetchMe.fulfilled, (state, action) => {
                state.userFullName = action.payload.firstName + " " + action.payload.lastName
            })
            .addMatcher(
                isAnyOf(
                    updateMemberPermissionsHandler.fulfilled,
                    updateManyMemberPermissionsHandler.fulfilled
                ),
                (state, action) => {
                    const payload = action.payload
                    if (!payload) {
                        const { workspaceId, member: argMember } = action.meta.arg
                        state.workspaceMembers[workspaceId] = state.workspaceMembers[
                            workspaceId
                        ]?.filter((member) => member.userId !== argMember.userId)
                        return
                    }
                    updateOneMemberHandler(state, action.payload)
                }
            ),
})

export {
    listMembersForWorkspace,
    listMembersForAllWorkspaces,
    getWorkspaceWithMostMembers,
    inviteAllUsersToWorkspace,
    removeMemberFromWorkspace,
    acceptInvitationsForUser,
    updateWorkspaceMember,
    updateWorkspaceMemberAccessRequest,
    setActiveMember,
    updateMemberPermissionsHandler,
    updateManyMemberPermissionsHandler,
    setInviteModalId,
    setRequestModalId,
    addWorkspaceId,
    selectWorkspaceIds,
    selectAllMembers,
    selectUserFullName,
    selectActiveMember,
    selectInviteModalId,
    selectRequestAccessModalId,
    selectWorkspaceMemberIds,
    selectUserFirstName,
    selectAllWorkspaceMembers,
}

export const reducer = { [name]: r } as Record<typeof name, typeof r>
